import { ErrorHandler, inject, Injectable } from '@angular/core';
import { AddressTypes } from '@innogy/customer-details/store';
import { getQueryParameterHistory } from '@core/jss-routing';
import { resetProgressiveForm } from '@innogy/shared/progressive-form';
import { selectUrl } from '@innogy/utils-state';
import { mapGenderToSalutation } from '@innogy/utils-deprecated';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { type FormGroupState, ResetAction } from 'ngrx-forms';
import { delay, filter, map, mergeMap, tap } from 'rxjs/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { MarkAsyncTasksAsPendingAction } from '@innogy/progressive-ngrx-forms';
import { getAppConfig } from '@core/config-store';
import { EnvConfigBrand } from '@core/config-models';
import type { FormAddress } from '@innogy/common-ui/forms';
import { FehApiActions } from '@feh/shared-api';
import type { SolarGetAddressInfoResponseSchema } from '@integration/api-client/feh';

import type { SolarPanelsProductWithCalculations } from '../models';
import { selectGetSolarPanelsNewCustomerAddressCheckData } from '../solar-panels-api';
import { fehSolarPanelsStoreSelectorKey } from '../feh-solar-panels.state';
import {
  clearSolarPanelsFunnelInitializedAction,
  handleSolarPanelsFunnelGenericErrorAction,
  onSolarPanelsFunnelNavigationAction,
  resetSolarPanelsProductSelectionAction,
  selectSelectedSolarPanelsProduct,
  selectSolarPanelsFunnelSettings,
} from '../solar-panels-funnel';
import { hideSolarPanelsProductOverviewAction } from '../solar-panels-products';
import {
  hideSolarPanelsSuitabilityCheckFormAction,
  resetSuitabilityFormAction,
} from '../solar-panels-suitability-check';
import {
  prefillSolarPanelsOrderFormAddressStateAction,
  resetSolarPanelsOrderFormAction,
} from './solar-panels-order-form.actions';
import {
  selectSolarPanelsOrderFormAddressFormState,
  selectSolarPanelsOrderFormPersonalDetailsFormState,
} from './solar-panels-order-form.selectors';
import {
  solarPanelsOrderAddressFormId,
  solarPanelsOrderConfirmationFormId,
  solarPanelsOrderPersonalDetailsFormId,
} from './solar-panels-order-form.reducer';
import type { SolarPanelsFunnelState } from '../solar-panels-funnel/solar-panels-funnel.reducer';
import type { PersonalDetails } from '../models/order-form.interface';

@Injectable()
export class SolarPanelsOrderFormEffects {
  private readonly actions$ = inject(Actions);
  private readonly store$ = inject(Store);
  private readonly errorHandler = inject(ErrorHandler);

  funnelSettings$ = this.store$.select(selectSolarPanelsFunnelSettings);
  addressCheckData$ = this.store$.select(
    selectGetSolarPanelsNewCustomerAddressCheckData
  );
  selectedProduct$ = this.store$.select(selectSelectedSolarPanelsProduct);
  solarPanelsOrderFormPersonalDetails$ = this.store$.select(
    selectSolarPanelsOrderFormPersonalDetailsFormState
  );
  solarPanelsOrderFormAddress$ = this.store$.select(
    selectSolarPanelsOrderFormAddressFormState
  );
  queryParamHistory$ = this.store$.select(getQueryParameterHistory);

  currentUrl$ = this.store$.select(selectUrl);

  public readonly prefillSolarPanelsAddressForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FehApiActions.getAddressinfoV1.successAction),
      mergeMap((action) => [
        prefillSolarPanelsOrderFormAddressStateAction({
          payload: {
            addressType: AddressTypes.ADDRESS,
            city: action.payload.address.city,
            houseNumberAddition: action.payload.address.housenumberaddition,
            postalCode: action.payload.address.postalcode,
            communicationNumber: action.payload.address.housenumber,
            street: action.payload.address.street,
          },
        }),
      ])
    )
  );

  public readonly onResetSolarPanelsAddressForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetSolarPanelsOrderFormAction),
      mergeMap(() => [
        new ResetAction(solarPanelsOrderAddressFormId),
        new ResetAction(solarPanelsOrderPersonalDetailsFormId),
      ])
    )
  );

  public readonly onSubmitSolarPanelsOrderForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<MarkAsyncTasksAsPendingAction>(MarkAsyncTasksAsPendingAction.TYPE),
      filter((action) => action.stepId === solarPanelsOrderConfirmationFormId),
      concatLatestFrom(() => [
        this.addressCheckData$,
        this.funnelSettings$,
        this.solarPanelsOrderFormPersonalDetails$,
        this.solarPanelsOrderFormAddress$,
        this.currentUrl$,
        this.queryParamHistory$,
        this.store$.select(getAppConfig),
      ]),
      filter(
        ([, addressCheckData, funnelSettings]) =>
          !!addressCheckData && !!funnelSettings.selectedProduct
      ),
      map(
        ([
          _,
          _addressCheckData,
          funnelSettings,
          personalDetails,
          address,
          currentUrl,
          queryParamHistory,
          appConfig,
        ]) => {
          const addressCheckData = _addressCheckData;
          const selectedProduct =
            funnelSettings.selectedProduct as SolarPanelsProductWithCalculations;

          const { amount: numberOfPanels } = selectedProduct.recommendation;

          return this.mapSubmitToCreateLead({
            personalDetails,
            address,
            brand: appConfig?.brand ?? 'essent',
            addressCheckData,
            selectedProduct,
            numberOfPanels,
            currentUrl,
            funnelSettings,
            queryParamHistory,
          });
        }
      )
    )
  );

  public readonly onSubmitSolarPanelsOrderFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FehApiActions.postSolarpanelV1.successAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(([, funnelSettings]) => !!funnelSettings.successPage),
      mergeMap(([, funnelSettings]) => [
        onSolarPanelsFunnelNavigationAction({
          page: funnelSettings.successPage,
        }),
      ])
    )
  );

  public readonly resetSolarOrderFormAndProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(
        ([{ payload }, funnelSettings]) =>
          payload.routerState.url === funnelSettings.successPage?.href
      ),
      // Even though we wait for navigated events, apparently
      // an extra delay is needed in order to prevent firing tool-step events
      // after form rest on the former page.
      // .5s is chosen arbitrarily, yet seems to work even under 3g conditions.
      delay(500),
      mergeMap(() => [
        resetSuitabilityFormAction(),
        hideSolarPanelsSuitabilityCheckFormAction(),
        resetSolarPanelsProductSelectionAction(),
        hideSolarPanelsProductOverviewAction(),
        resetSolarPanelsOrderFormAction(),
        clearSolarPanelsFunnelInitializedAction(),
        resetProgressiveForm({ formId: fehSolarPanelsStoreSelectorKey }),
      ])
    )
  );

  public readonly onSubmitSolarPanelsOrderFormError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FehApiActions.postSolarpanelV1.errorAction),
      tap(({ payload }) => {
        this.errorHandler.handleError(
          new Error(
            'Error while submitting solar order' + JSON.stringify(payload.error)
          )
        );
      }),
      map(handleSolarPanelsFunnelGenericErrorAction)
    )
  );

  private mapSubmitToCreateLead(p: {
    personalDetails: FormGroupState<PersonalDetails>;
    address: FormGroupState<FormAddress>;
    brand: EnvConfigBrand;
    addressCheckData: SolarGetAddressInfoResponseSchema | undefined;
    selectedProduct: SolarPanelsProductWithCalculations;
    numberOfPanels: number;
    currentUrl: string;
    funnelSettings: SolarPanelsFunnelState;
    queryParamHistory: { [key: string]: string };
  }) {
    return FehApiActions.postSolarpanelV1.requestAction({
      payload: {
        requestBody: {
          type: 'solarPanel',
          lead: {
            title:
              mapGenderToSalutation(p.personalDetails.value.salutation) ?? '',
            initials: p.personalDetails.value.initials,
            prefix: p.personalDetails.value.prefix,
            lastName: p.personalDetails.value.lastName,
            addresses: {
              communication: {
                postcode: p.address.value.postalCode,
                street: p.address.value.street,
                houseNumber: `${p.address.value.communicationNumber}`,
                houseNumberExtension: p.address.value.houseNumberAddition,
                city: p.address.value.city,
              },
            },
            phone: p.personalDetails.value.phoneNumber,
            email: p.personalDetails.value.emailAddress,
          },
          context: {
            channel: 'Online',
            requestType: 'adviesgesprek',
            servicePartnerBranch: 'adviesgesprek',
            brand: p.brand === EnvConfigBrand.ED ? 'energiedirect' : 'essent',
            leadContractType: 'Koop',
          },
          metadata: {
            temperature: 4,
            createdOn: new Date().toISOString(),
            totalPlantSurface: p.addressCheckData?.building.roofArea.total ?? 0,
            yearlyIncome: p.selectedProduct.yearlySavings,
            energyConsumption:
              p.addressCheckData?.calculationParameters.consumption ?? 0,
            panelType: p.selectedProduct.suiteCrmId,
            yearlyYield: p.selectedProduct.recommendation.yearlyYield,
            costExclVat: p.numberOfPanels * p.selectedProduct.unitPrice,
            source: 'Online',
            environment: 'Consument',
            referrer: p.currentUrl,
            leadTrackingId: 'DirectOther',
            e2eTrackingId: p.funnelSettings.trackingId ?? '',
            gclid: p.queryParamHistory['gclid'] ?? '',
            interestedInHomeBattery:
              p.personalDetails.value.homeBatteryInterest,
          },
          products: [
            {
              category: p.brand === EnvConfigBrand.ED ? 'SolarED' : 'Solar',
              quantity: p.numberOfPanels,
            },
          ],
        },
      },
    });
  }
}
